import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { 
  Plus, Search, Eye, Loader2, AlertCircle, RefreshCw, 
  DollarSign, CheckCircle, XCircle, Clock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: '',
    status: ''
  });
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    loadMasterData();
    loadPayments();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const custRes = await axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }));
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadPayments = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`${BACKEND_URL}/api/ar/payments?${params.toString()}`);
      setPayments(response.data || []);
    } catch (error) {
      console.error('Error loading payments:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load payments: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadPayments();
  };

  const handleClearFilters = () => {
    setFilters({ customer_id: '', status: '' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'POSTED': return 'bg-green-100 text-green-800';
      case 'CLEARED': return 'bg-orange-100 text-orange-800';
      case 'REVERSED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'POSTED': case 'CLEARED': return <CheckCircle className="w-4 h-4" />;
      case 'REVERSED': return <XCircle className="w-4 h-4" />;
      case 'DRAFT': return <Clock className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'BANK_TRANSFER': 'Bank Transfer',
      'CHECK': 'Check',
      'CASH': 'Cash',
      'CREDIT_CARD': 'Credit Card',
      'DIRECT_DEBIT': 'Direct Debit'
    };
    return labels[method] || method;
  };

  const totalPosted = payments
    .filter(p => p.status === 'POSTED')
    .reduce((sum, p) => sum + (p.payment_amount || 0), 0);

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Payments</h1>
            <p className="text-sm text-gray-600 mt-1">Manage incoming payments and clearing</p>
          </div>
          {canWrite('ar_payment') && (
          <Button onClick={() => navigate('/ar/payments/create')} data-testid="create-payment-btn">
            <Plus className="w-4 h-4 mr-2" />
            Post Payment
          </Button>
          )}
        </div>

        {/* Summary Card */}
        <Card className="p-4 mb-6 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <DollarSign className="w-10 h-10 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Total Posted Payments</p>
                <p className="text-2xl font-bold text-green-800">
                  USD {totalPosted.toLocaleString(undefined, {minimumFractionDigits: 2})}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Payments</p>
              <p className="text-xl font-bold text-gray-800">{payments.length}</p>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="POSTED">Posted</SelectItem>
                  <SelectItem value="REVERSED">Reversed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </Card>

        {/* Payments Table */}
        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading payments...</span>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No payments found.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Post Payment" to record a new incoming payment.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payments-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Payment #</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Customer</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Payment Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Method</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Allocated</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.payment_number} className="border-b hover:bg-gray-50" data-testid={`payment-${payment.payment_number}`}>
                      <td className="py-3 px-4">
                        <span className="font-mono font-bold text-green-600">{payment.payment_number}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{getCustomerName(payment.customer_id)}</p>
                          <p className="text-xs text-gray-500">{payment.customer_id}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{payment.payment_date?.split('T')[0]}</td>
                      <td className="py-3 px-4">{getPaymentMethodLabel(payment.payment_method)}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold">
                        {payment.currency} {payment.payment_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </td>
                      <td className="py-3 px-4 text-right font-mono">
                        {payment.currency} {payment.allocated_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${getStatusColor(payment.status)}`}>
                          {getStatusIcon(payment.status)}
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/ar/payments/${payment.payment_number}`)} title="View Payment">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentList;
